import React, { useState } from 'react';

import Lozenge from '@atlaskit/lozenge';
import {
  UnmatchedCommitAuthorAvatar,
  UnmatchedCommitAuthorName,
} from '@atlassian/bitbucket-user-profile';
import { RelativeDate } from '@atlassian/bitkit-date';

import { Commit } from 'src/components/types';
import { UserAvatar } from 'src/containers/user-avatar';
import { UserName } from 'src/containers/user-name';
import { useIntl } from 'src/hooks/intl';
import { shortHash } from 'src/utils/short-hash';

import { UpdateCommitAction } from '../hooks/commit-page-reducer';

import messages from './commit-page.i18n';
import * as styles from './commit-page.styled';
import { CopyHashIcon } from './copy-hash-icon';
import { HeaderActions } from './header-actions';

type CommitDetailsProps = {
  commit: Commit;
  dispatchCommitUpdate: (action: UpdateCommitAction) => void;
};

/**
 * The section of the commit page that isn't the diff or the sidebar.
 * Contains the breadcrumbs, author info, and commit message.
 */
export const CommitDetails = ({
  commit,
  dispatchCommitUpdate,
}: CommitDetailsProps) => {
  const { user, raw: rawUser } = commit.author;
  const intl = useIntl();
  const isMerge = (commit.parents?.length || 0) > 1;
  const isMergeWithConflicts = isMerge && commit.message.includes('Conflicts:');
  const [isHovered, setIsHovered] = useState(false);

  return (
    <styles.AuthorInfo>
      <styles.AuthorAndCommitContainer>
        {user ? (
          <UserAvatar user={user} profileCardPosition="bottom-start" />
        ) : (
          <UnmatchedCommitAuthorAvatar rawCommitAuthor={rawUser} />
        )}
        <styles.CommitDetails>
          <styles.HashContainer
            tabIndex={0}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onFocus={() => setIsHovered(true)}
            onBlur={() => setIsHovered(false)}
          >
            <strong>
              {user ? (
                <UserName profileCardPosition="bottom-start" user={user} />
              ) : (
                <UnmatchedCommitAuthorName rawCommitAuthor={rawUser} />
              )}
            </strong>
            &nbsp;
            {intl.formatMessage(messages.committed)}&nbsp;
            <strong>{shortHash(commit.hash)}</strong>
            {isMerge && (
              <styles.LozengeWrapper>
                <Lozenge isBold>
                  {isMergeWithConflicts
                    ? `${intl.formatMessage(messages.isMergeWithConflicts)}`
                    : `${intl.formatMessage(messages.isMerge)}`}
                </Lozenge>
              </styles.LozengeWrapper>
            )}
            <CopyHashIcon isHovered={isHovered} commit={commit} />
          </styles.HashContainer>
          <RelativeDate
            key={`${commit.date}-${commit.hash}`}
            date={commit.date}
          />
        </styles.CommitDetails>
      </styles.AuthorAndCommitContainer>
      <HeaderActions
        dispatchCommitUpdate={dispatchCommitUpdate}
        commit={commit}
      />
    </styles.AuthorInfo>
  );
};
